'use client';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { UnauthQueryOptions } from '@/api';
import { DesktopPageShell } from '@/components/template/DesktopPageShell';

import { PAGE_SIZE } from '../../constants';
import { buildPaginationMeta } from '../../utils';
import { SearchContentsCardSkeletonList } from '../SearchContentsCardSkeleton';
import { SearchSectionShell } from '../SearchSectionShell';
import { HometownNewsFilter } from './HometownNewsFilter';
import { HometownNewsList } from './HometownNewsList';

interface HometownNewsSearchProps {
  keyword: string;
  page: number;
  onPageChange: (page: number) => void;
  isFilterOpen: boolean;
  onCloseFilter: () => void;
  regionCode: string | null;
  subRegionCode: string | null;
  onRegionChange: (code: string | null) => void;
  onSubRegionChange: (code: string | null) => void;
  onReset: () => void;
}

export function HometownNewsSearch({
  keyword,
  page,
  onPageChange,
  isFilterOpen,
  onCloseFilter,
  regionCode,
  subRegionCode,
  onRegionChange,
  onSubRegionChange,
  onReset,
}: HometownNewsSearchProps) {
  const { data, isLoading } = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/query/hometown-news']({
      query: {
        searchKeyword: keyword,
        regionCode: regionCode || undefined,
        subRegionCode: subRegionCode || undefined,
        hometownNewsSort: 'LATEST',
        page,
        size: PAGE_SIZE,
      },
    }),
    placeholderData: keepPreviousData,
  });

  const meta = buildPaginationMeta(data?.context?.totalCount ?? 0);

  return (
    <DesktopPageShell
      className="mt-20 w-full mobile-tablet:mt-0"
      leftAside={
        <HometownNewsFilter
          isDrawerOpen={isFilterOpen}
          onCloseDrawer={onCloseFilter}
          onApplyDrawer={onCloseFilter}
          regionCode={regionCode}
          subRegionCode={subRegionCode}
          onRegionChange={onRegionChange}
          onSubRegionChange={onSubRegionChange}
          onReset={onReset}
        />
      }
    >
      <SearchSectionShell
        meta={meta}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={onPageChange}
      >
        {isLoading ? (
          <SearchContentsCardSkeletonList count={PAGE_SIZE} />
        ) : (
          <HometownNewsList items={data?.context?.contents ?? []} />
        )}
      </SearchSectionShell>
    </DesktopPageShell>
  );
}
